import { ChangeEvent, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';

import setWorkflowPriority from 'store/setWorkflowPriority';

export const PRIORITY_BANDS = [
  { label: 'Low', value: -10 },
  { label: 'Normal', value: 0 },
  { label: 'High', value: 10 },
] as const;

const BAND_VALUES: number[] = PRIORITY_BANDS.map((b) => b.value);

export function bandLabel(priority: number): string {
  const band = PRIORITY_BANDS.find((b) => b.value === priority);
  return band ? band.label : `Custom (${priority})`;
}

interface Props {
  runId: string;
  priority: number;
}

export default function PriorityBandSelector({ runId, priority }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyNow, setApplyNow] = useState(false);
  const isKnownBand = BAND_VALUES.includes(priority);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value);
    if (next === priority) return;
    setSaving(true);
    setWorkflowPriority(runId, next, applyNow)
      .catch((err) => {
        console.error('Failed to set workflow priority:', err);
        setError('Failed to update priority. Please try again.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <TextField
        id={`priority-${runId}`}
        label="Priority"
        select
        size="small"
        disabled={saving}
        value={priority}
        onChange={onChange}
        sx={{ minWidth: 140 }}
      >
        {!isKnownBand && <MenuItem value={priority}>{bandLabel(priority)}</MenuItem>}
        {PRIORITY_BANDS.map((b) => (
          <MenuItem key={b.value} value={b.value}>
            {b.label}
          </MenuItem>
        ))}
      </TextField>
      <Tooltip
        describeChild
        title="Also yield on change: running batch tasks stop and requeue at the new priority instead of finishing at the old one"
        placement="bottom-start"
      >
        <FormControlLabel
          control={<Checkbox size="small" checked={applyNow} onChange={(e) => setApplyNow(e.target.checked)} />}
          label="Apply to running"
          sx={{ mr: 0 }}
        />
      </Tooltip>
      <Snackbar open={error !== null} autoHideDuration={4000} onClose={() => setError(null)} message={error} />
    </>
  );
}
